import { getLocalStorage } from "@elit-hair/elit-tools";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { toast } from "sonner";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { AudioDevice, AudioDeviceListDiff } from "@/features/audio";
import { isAudioDeviceKind } from "@/utils/audio-device-storage.utils";

function notifyDeviceListDiff(diff: AudioDeviceListDiff, t: TFunction): void {
    const storedInputId = getLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key,
        null
    );
    const storedOutputId = getLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key,
        null
    );

    for (const device of diff.removed) {
        const label = device.label?.trim() || t("incident_audio_device_unnamed");
        const isSelectedInput = device.id === storedInputId?.trim();
        const isSelectedOutput = device.id === storedOutputId?.trim();

        if (isSelectedInput) {
            toast.warning(t("audio_device_selected_input_disconnected", { device: label }));
            continue;
        }

        if (isSelectedOutput) {
            toast.warning(t("audio_device_selected_output_disconnected", { device: label }));
            continue;
        }

        if (device.kind === "audioinput") {
            toast.info(t("audio_device_input_disconnected", { device: label }));
        } else {
            toast.info(t("audio_device_output_disconnected", { device: label }));
        }
    }

    for (const device of diff.added) {
        const label = device.label?.trim() || t("incident_audio_device_unnamed");

        if (device.kind === "audioinput") {
            toast.info(t("audio_device_input_connected", { device: label }));
        } else {
            toast.info(t("audio_device_output_connected", { device: label }));
        }
    }
}

const toMediaDeviceInfo = (device: AudioDevice): MediaDeviceInfo => ({
    deviceId: device.id,
    kind: device.kind,
    label: device.label,
    groupId: "",
    toJSON: () => ({}),
});

export function useAudioDeviceChangeToasts() {
    const showDeviceChangeToasts = useCallback((diff: AudioDeviceListDiff, t: TFunction) => {
        notifyDeviceListDiff(diff, t);
    }, []);

    const showDeviceChangeToastsFromLists = useCallback(
        (previousDevices: MediaDeviceInfo[] | null, currentDevices: MediaDeviceInfo[], t: TFunction) => {
            const previousIds = new Set(previousDevices?.map((device) => device.deviceId) ?? []);
            const currentIds = new Set(currentDevices.map((device) => device.deviceId));

            const filteredDevices: AudioDevice[] = currentDevices
                .map((device) => {
                    if (!isAudioDeviceKind(device.kind)) {
                        return null;
                    }
                    return {
                        id: device.deviceId,
                        label: device.label,
                        kind: device.kind,
                        isDefault: false,
                    };
                })
                .filter((device) => device !== null);

            showDeviceChangeToasts(
                {
                    added: filteredDevices
                        .filter((device) => !previousIds.has(device.id))
                        .map((device) => ({
                            id: device.id,
                            label: device.label,
                            kind: device.kind,
                            isDefault: false,
                        })),
                    removed: (filteredDevices ?? [])
                        .filter((device) => !currentIds.has(device.id))
                        .map((device) => ({
                            id: device.id,
                            label: device.label,
                            kind: device.kind,
                            isDefault: false,
                        })),
                },
                t
            );
        },
        [showDeviceChangeToasts]
    );

    return { showDeviceChangeToasts, showDeviceChangeToastsFromLists, toMediaDeviceInfo };
}
